import { differenceInMinutes, formatDistanceToNow, parseISO } from "date-fns";
import { AvailableVendor } from "hooks/useLogisticsTicketVendors";
import { formatCentsAmount } from "utils/currency";

const VENDOR_COLORS = ["#1E88E5", "#43A047", "#FB8C00", "#8E24AA", "#546E7A"];

export type VendorQuoteResponseStatus = "received" | "awaiting" | "declined";

export type VendorQuoteRow = {
  id: string;
  name: string;
  contactName?: string;
  email: string;
  phone?: string;
  vendorType: string;
  priceLabel: string;
  priceInCents: number;
  quoteAmountInCents?: number;
  proposedDate?: string;
  proposedTime?: string;
  initial: string;
  color: string;
  sentAt?: string;
  customerSentAt?: string;
  portalUrl?: string;
  hasQuote: boolean;
  isDeclined: boolean;
  isAssigned: boolean;
  responseStatus: VendorQuoteResponseStatus | null;
};

export function formatPriceAmount(amountInCents: number): string {
  return formatCentsAmount(amountInCents);
}

export function formatVendorPrice(amountInCents: number, vendorName?: string): string {
  if (amountInCents === 0 || vendorName?.toLowerCase().includes("revivn")) {
    return "";
  }

  return formatPriceAmount(amountInCents);
}

export function formatQuotedPrice(amountInCents: number): string {
  return `${formatPriceAmount(amountInCents)} quoted`;
}

export function formatSentLabel(sentAt: string): string {
  const sentDate = parseISO(sentAt);

  if (differenceInMinutes(new Date(), sentDate) < 1) {
    return "just now";
  }

  return `${formatDistanceToNow(sentDate, { addSuffix: false })} ago`;
}

function getResponseStatus(quoteRequest?: AvailableVendor["quoteRequest"]): VendorQuoteResponseStatus | null {
  if (!quoteRequest?.sentAt) {
    return null;
  }

  if (quoteRequest.assigned) {
    return "awaiting";
  }

  if (quoteRequest.quote) {
    return "received";
  }

  if (quoteRequest.declinedAt) {
    return "declined";
  }

  return "awaiting";
}

export function buildVendorQuoteRows(vendors: AvailableVendor[]): VendorQuoteRow[] {
  return vendors.map((vendor, index) => {
    const quoteRequest = vendor.quoteRequest;
    const quoteAmountInCents = quoteRequest?.quote?.amountInCents;
    const listAmountInCents = quoteAmountInCents ?? 0;

    return {
      id: vendor.uuid,
      name: vendor.name,
      contactName: vendor.contactName,
      email: vendor.email,
      phone: vendor.phone,
      vendorType: vendor.vendorType ?? (vendor.name.toLowerCase().includes("revivn") ? "Internal Fleet" : "Freight"),
      priceLabel: formatVendorPrice(listAmountInCents, vendor.name),
      priceInCents: listAmountInCents,
      quoteAmountInCents,
      proposedDate: quoteRequest?.quote?.proposedDate,
      proposedTime: quoteRequest?.quote?.proposedTime ?? quoteRequest?.quote?.proposedTimeWindow,
      initial: vendor.name.charAt(0).toUpperCase(),
      color: VENDOR_COLORS[index % VENDOR_COLORS.length],
      sentAt: quoteRequest?.sentAt,
      customerSentAt: quoteRequest?.customerSentAt,
      portalUrl: quoteRequest?.hermesVendorQuoteRequestUrl,
      hasQuote: Boolean(quoteRequest?.quote),
      isDeclined: Boolean(quoteRequest?.declinedAt),
      isAssigned: Boolean(quoteRequest?.assigned),
      responseStatus: getResponseStatus(quoteRequest),
    };
  });
}
